import React from 'react';
import { Target, ListChecks, TrendUp, WarningCircle, Database, ChartBar, Users, Clock, Certificate, BookOpen } from '@phosphor-icons/react';

interface KPIData {
    cyclesCount?: number;
    goalsCount?: number;
    pipsCount?: number;
    cycles?: number;
    goals?: number;
    pips?: number;
    [key: string]: number | string | undefined;
}

/** Shape produced by WidgetWrapper's inline multi_stat transform */
interface StatsEntry {
    label: string;
    value: number;
}

type DataInput =
    | KPIData
    | KPIData[]
    | StatsEntry[]
    | { data: KPIData | KPIData[] | StatsEntry[]; metadata?: any };

interface MultiKPIProps {
    data: DataInput;
    options?: {
        show_icons?: boolean;
        show_labels?: boolean;
    };
    kpis?: Array<{
        key: string;
        label: string;
        icon: string;
    }>;
}

const getIcon = (iconKey: string) => {
    switch (iconKey) {
        case 'cycle':
        case 'cycles':
            return <ListChecks size={24} />;
        case 'target':
        case 'goals':
            return <Target size={24} />;
        case 'growth':
        case 'idp':
            return <TrendUp size={24} />;
        case 'improvement':
        case 'pip':
        case 'pips':
            return <WarningCircle size={24} />;
        case 'database':
        case 'total':
            return <Database size={24} />;
        case 'chart':
        case 'progress':
            return <ChartBar size={24} />;
        case 'users':
        case 'learners':
            return <Users size={24} />;
        case 'clock':
        case 'time':
            return <Clock size={24} />;
        case 'certificate':
            return <Certificate size={24} />;
        case 'book':
        case 'courses':
            return <BookOpen size={24} />;
        default:
            return <Target size={24} />;
    }
};

/** Pick a sensible icon based on the stat label text */
const inferIcon = (label: string): string => {
    const lower = label.toLowerCase();
    if (lower.includes('total') || lower.includes('count')) return 'database';
    if (lower.includes('learner') || lower.includes('user') || lower.includes('employee')) return 'users';
    if (lower.includes('time') || lower.includes('hour')) return 'clock';
    if (lower.includes('progress') || lower.includes('rate') || lower.includes('completion')) return 'chart';
    if (lower.includes('certificate')) return 'certificate';
    if (lower.includes('course') || lower.includes('catalog') || lower.includes('enroll')) return 'book';
    if (lower.includes('cycle') || lower.includes('review')) return 'cycle';
    if (lower.includes('goal')) return 'target';
    if (lower.includes('pip') || lower.includes('overdue') || lower.includes('stale')) return 'improvement';
    return 'target';
};

const getValue = (data: KPIData, key: string): number | string => {
    if (data[key] !== undefined) return data[key] as number | string;

    // Try with Count suffix (e.g., 'cycles' -> 'cyclesCount')
    if (data[`${key}Count`] !== undefined) return data[`${key}Count`] as number;

    return 0;
};

/** Detect if data is a stats array [{label, value}, ...] from WidgetWrapper */
const isStatsArray = (data: unknown): data is StatsEntry[] =>
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'label' in data[0] &&
    'value' in data[0];

// Unwrap data from various API response formats
const unwrapData = (data: DataInput): KPIData | StatsEntry[] | null => {
    if (!data) return null;

    // If data is wrapped in { data: ... }
    if (typeof data === 'object' && 'data' in data && !Array.isArray(data)) {
        const innerData = (data as { data: KPIData | KPIData[] | StatsEntry[] }).data;
        if (isStatsArray(innerData)) return innerData;
        if (Array.isArray(innerData)) return innerData[0] || null;
        return innerData;
    }

    // If data is a stats array [{label, value}, ...], return as-is
    if (isStatsArray(data)) return data;

    // If data is an array, take first element
    if (Array.isArray(data)) return data[0] || null;

    // Data is already a direct object
    return data as KPIData;
};

const PERFORMSPARK_DEFAULT_KPIS = [
    { key: 'cycles', label: 'Review Cycles', icon: 'cycle' },
    { key: 'goals', label: 'Goals', icon: 'target' },
    { key: 'pips', label: 'PIPs', icon: 'improvement' },
];

export const MultiKPI: React.FC<MultiKPIProps> = ({ data, options = {}, kpis = [] }) => {
    const { show_icons = true, show_labels = true } = options;

    const unwrapped = unwrapData(data);

    if (!unwrapped) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No data available</div>;
    }

    // ── Stats array path: data is [{label, value}, ...] from WidgetWrapper ──
    if (isStatsArray(unwrapped)) {
        const gridCols = unwrapped.length <= 2 ? 'grid-cols-2' :
                         unwrapped.length === 3 ? 'grid-cols-3' :
                         unwrapped.length <= 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' :
                         'grid-cols-2 md:grid-cols-4';

        return (
            <div className={`grid ${gridCols} gap-3 p-4 h-full content-center`}>
                {unwrapped.map((stat) => (
                    <div
                        key={stat.label}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 border border-gray-100"
                    >
                        {show_icons && (
                            <div className="mb-2 text-blue-600">
                                {getIcon(inferIcon(stat.label))}
                            </div>
                        )}
                        <span className="text-2xl font-bold text-gray-900">
                            {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                        </span>
                        {show_labels && (
                            <span className="text-xs font-medium text-gray-500 mt-1 text-center">
                                {stat.label}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    // ── KPI object path: data is {cycles, goals, pips, ...} ─────────────────
    const unwrappedData = unwrapped as KPIData;

    // Use explicit kpis prop if provided; otherwise auto-detect from data keys
    let displayKpis: Array<{ key: string; label: string; icon: string }>;

    if (kpis.length > 0) {
        displayKpis = kpis;
    } else {
        // Check if data has PerformSpark shape (cycles/goals/pips keys)
        const hasPerformSparkKeys = unwrappedData.cycles !== undefined ||
                                     unwrappedData.goals !== undefined ||
                                     unwrappedData.pips !== undefined ||
                                     unwrappedData.cyclesCount !== undefined ||
                                     unwrappedData.goalsCount !== undefined ||
                                     unwrappedData.pipsCount !== undefined;

        if (hasPerformSparkKeys) {
            displayKpis = PERFORMSPARK_DEFAULT_KPIS;
        } else {
            // Auto-derive KPIs from data keys (Learn widgets, etc.)
            displayKpis = Object.entries(unwrappedData)
                .filter(([_, v]) => typeof v === 'number' || (typeof v === 'string' && !isNaN(Number(v))))
                .map(([key]) => ({
                    key,
                    label: key
                        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
                    icon: inferIcon(key),
                }));
        }
    }

    if (displayKpis.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No data available</div>;
    }

    const gridCols = displayKpis.length <= 2 ? 'grid-cols-2' :
                     displayKpis.length === 3 ? 'grid-cols-3' :
                     displayKpis.length <= 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' :
                     'grid-cols-2 md:grid-cols-4';

    return (
        <div className={`grid ${gridCols} gap-3 p-4 h-full content-center`}>
            {displayKpis.map((kpi) => (
                <div
                    key={kpi.key}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 border border-gray-100"
                >
                    {show_icons && (
                        <div className="mb-2 text-blue-600">
                            {getIcon(kpi.icon)}
                        </div>
                    )}
                    <span className="text-2xl font-bold text-gray-900">
                        {(() => {
                            const val = getValue(unwrappedData, kpi.key);
                            return typeof val === 'number' ? val.toLocaleString() : val;
                        })()}
                    </span>
                    {show_labels && (
                        <span className="text-xs font-medium text-gray-500 mt-1 text-center">
                            {kpi.label}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
};
